/* =============================================================================
 * PDP Ask AI (outywjjt) — conversational Ask AI widget with FAQ chips
 *
 * Ask-only sibling of f4qpdp01. Suggestion chips are drawn from the product's
 * FAQ metafield (live on storefront, draft in preview — the wrapper picks the
 * state and hands the items in via entry.content.items). The runtime adds:
 *
 *   1. <sai-outywjjt> custom element with applyVariant(presentation, content)
 *      so cross-experience / content-experiment swaps update the header,
 *      subheading, placeholder, branding, mark gradient, and chips.
 *   2. __spectrumAi.snippet.bind(node, callback) for variant resolution AND
 *      to capture the analytics `track` handle.
 *   3. One analytics event through track (faqs:ask), fired before the request.
 *   4. The Ask AI chat flow (POST {proxyBase}/faq/ask): the shopper's question
 *      renders as a user bubble, a typing indicator shows while the answer
 *      loads, then the answer renders as a chat bubble with AI follow-up chips
 *      (or an error bubble with Retry).
 *
 * Suggestion chips: a RANDOM `data-max-chips` subset of the FAQ questions,
 * reshuffled on load so different questions surface. SSR renders the first N;
 * this runtime reshuffles. When the product has no FAQs the chips are hidden.
 *
 * Only one exchange is shown at a time — each new question replaces the turn.
 *
 * No-bind fallback: if window.__spectrumAi is absent the widget hides itself.
 *
 * Trust boundary: the answer HTML from /faq/ask is server-rendered and trusted;
 * it is assigned via innerHTML (see packages/snippet-library/CLAUDE.md § JS).
 * ============================================================================= */

;(() => {
  if (window.__sai_outywjjt_initialized__) return
  window.__sai_outywjjt_initialized__ = true

  const SNIPPET_ID = 'outywjjt'
  const TAG = 'sai-outywjjt'
  const FEATURE_SLUG = 'faqs'

  const RETRY_SVG =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" focusable="false" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></svg>'
  // Built-in mark icon — must match the SSR sparkle in the .liquid so a broken
  // custom icon (icon_url) falls back to the same glyph.
  const SPARKLE_SVG =
    '<svg viewBox="0 -2 18 18" fill="none" focusable="false"><path d="M9 1.5L10.2 5.8L14.5 7L10.2 8.2L9 12.5L7.8 8.2L3.5 7L7.8 5.8L9 1.5Z" fill="currentColor"/></svg>'
  const FAQ_CAPABILITY_TIMEOUT_MS = 5000

  function noopTrack() {}

  function recordFaqCapabilityFailure(spectrumAi, error) {
    const diagnostic = {
      status: 'unavailable',
      detail: error instanceof Error ? error.message : String(error),
    }
    spectrumAi.faqCapabilityDiagnostic = diagnostic
    window.dispatchEvent(
      new CustomEvent('spectrum:faq-capability-unavailable', { detail: diagnostic }),
    )
    return false
  }

  // Share the brand-level capability read with combined FAQ widgets. The
  // promise resolves false for transport, HTTP, or payload failures so the
  // shopper never sees an ask control that the server cannot honor.
  function getFaqCapabilityPromise(spectrumAi, proxyBase) {
    if (!spectrumAi) return Promise.resolve(false)
    if (!spectrumAi.faqCapabilityPromise) {
      const controller = new AbortController()
      const timeoutId = setTimeout(
        () => controller.abort(new Error('FAQ capability request timed out')),
        FAQ_CAPABILITY_TIMEOUT_MS,
      )
      spectrumAi.faqCapabilityPromise = fetch(`${proxyBase}/faq/capability`, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`FAQ capability request failed: ${response.status}`)
          return response.json()
        })
        .then((payload) => {
          if (
            !payload ||
            payload.success !== true ||
            !payload.data ||
            typeof payload.data.enabled !== 'boolean'
          ) {
            throw new Error('Malformed FAQ capability payload')
          }
          return payload.data.enabled
        })
        .catch((error) => recordFaqCapabilityFailure(spectrumAi, error))
        .finally(() => clearTimeout(timeoutId))
    }
    return spectrumAi.faqCapabilityPromise
  }

  // safeTrack wraps every track call so a malformed payload or downstream
  // analytics bug never breaks the Ask AI form.
  function safeTrack(track) {
    return (name, payload) => {
      try {
        track(name, payload)
      } catch (_) {
        /* swallow — analytics is best-effort */
      }
    }
  }

  function el(tag, className, attrs) {
    const node = document.createElement(tag)
    if (className) node.className = className
    if (attrs) for (const k of Object.keys(attrs)) node.setAttribute(k, attrs[k])
    return node
  }

  // Fisher–Yates shuffle — returns a new array.
  function shuffle(arr) {
    const a = arr.slice()
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      const t = a[i]
      a[i] = a[j]
      a[j] = t
    }
    return a
  }

  if (!customElements.get(TAG)) {
    class SaiAskAi extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return
        this._initialized = true

        // Defaults; replaced via setAnalytics() once bindAllContainers runs.
        this._track = noopTrack
        this._faqCapabilityEnabled = false
        this._localShowBranding = this.dataset.showBranding !== 'false'
        // True once the shopper has asked something — guards the resting chips
        // from being re-rendered over an active conversation on variant swaps.
        this._conversing = false
        // The FAQ question pool — drives both the resting and follow-up chips.
        this._faqPool = []

        this._syncCapabilityVisibility()
        this._initIconFallback()
        this._initAsk()
      }

      // Called once per page-load by bindAllContainers, before the bind
      // callback fires. Until then, all tracking is a noop.
      setAnalytics(track) {
        this._track = typeof track === 'function' ? safeTrack(track) : noopTrack
      }

      _syncCapabilityVisibility() {
        const card = this.querySelector('[data-spectrum-ai-required]')
        if (card) card.hidden = !this._faqCapabilityEnabled

        const footer = this.querySelector('[data-branding]')
        if (footer) {
          footer.hidden = !(this._faqCapabilityEnabled && this._localShowBranding)
        }
      }

      _initAsk() {
        this._ask = null
        this._faqPool = this._readFaqPool()
        const spectrumAi = window.__spectrumAi || null

        if (!spectrumAi) {
          // No Spectrum AI — hide the widget; nothing to fall back to.
          this._syncCapabilityVisibility()
          return
        }

        const form = this.querySelector('[data-action="ask"]')
        const input = form ? form.querySelector('.sai-outywjjt__input') : null
        const sendBtn = form ? form.querySelector('.sai-outywjjt__send') : null
        const body = this.querySelector('[data-slot="body"]')

        if (!form || !input || !body) return

        // Resolve the brand-specific app-proxy base. The Spectrum AI app embed
        // publishes window.__spectrumAi.baseProxyUrl (e.g. /apps/spectrum_ai_<org>),
        // which is authoritative. Fall back to the data attribute, then the literal.
        const proxyBase =
          spectrumAi?.baseProxyUrl || this.getAttribute('data-proxy-base') || '/apps/spectrum'

        this._ask = {
          form,
          input,
          sendBtn,
          body,
          proxyBase,
          productId: Number(this.getAttribute('data-product-id')),
          productHandle: this.getAttribute('data-product-handle') || '',
          rateLimitedUntil: 0,
          inflight: false,
        }

        getFaqCapabilityPromise(spectrumAi, proxyBase).then((enabled) => {
          this._faqCapabilityEnabled = enabled === true
          this._syncCapabilityVisibility()
        })

        form.addEventListener('submit', (e) => {
          e.preventDefault()
          this._ask_submit(input.value)
        })

        // Delegated handler for suggested-question chips (FAQ + follow-up) and
        // the retry button — all carry data-question and just re-ask (which
        // replaces the current exchange).
        this.addEventListener('click', (e) => {
          const btn = e.target.closest?.('[data-question]')
          if (btn && this.contains(btn)) {
            e.preventDefault()
            this._ask_submit(btn.getAttribute('data-question') || '')
          }
        })

        // Reshuffle the resting chips from the full FAQ pool on load.
        this._renderStarterChips()
      }

      // If a custom icon (icon_url) was rendered as <img> and it fails to load
      // (or already failed before the runtime attached), swap the mark back to
      // the built-in sparkle so a broken URL never leaves a broken image.
      _initIconFallback() {
        const mark = this.querySelector('.sai-outywjjt__avatar')
        const img = mark && mark.querySelector('img')
        if (!img) return
        const toSparkle = () => {
          mark.innerHTML = SPARKLE_SVG
        }
        img.addEventListener('error', toSparkle, { once: true })
        if (img.complete && img.naturalWidth === 0) toSparkle()
      }

      _readFaqPool() {
        const node = this.querySelector('[data-slot="faqs"]')
        if (!node) return []
        try {
          const arr = JSON.parse(node.textContent || '[]')
          return Array.isArray(arr) ? arr.filter((q) => typeof q === 'string' && q.trim()) : []
        } catch (_) {
          return []
        }
      }

      _maxChips() {
        return Number.parseInt(this.dataset.maxChips || '4', 10) || 4
      }

      // The AI mark icon (svg or custom <img>) rendered in the header, reused
      // for the in-chat avatars so a custom icon / gradient flows through.
      _markHtml() {
        const mark = this.querySelector('.sai-outywjjt__avatar')
        return mark ? mark.innerHTML : ''
      }

      _botAvatar() {
        const a = el('div', 'sai-outywjjt__bot-avatar')
        a.innerHTML = this._markHtml()
        return a
      }

      _buildChips(label, questions, max) {
        const cap = max && max > 0 ? max : questions.length
        const items = (questions || [])
          .filter((t) => typeof t === 'string' && t.trim())
          .slice(0, cap)
        if (!items.length) return null
        const wrap = el('div', 'sai-outywjjt__chips')
        const lbl = el('div', 'sai-outywjjt__chips-label')
        lbl.textContent = label
        wrap.appendChild(lbl)
        const list = el('div', 'sai-outywjjt__chip-list')
        for (const q of items) {
          const text = q.trim()
          const chip = el('button', 'sai-outywjjt__chip', { type: 'button', 'data-question': text })
          chip.textContent = text
          list.appendChild(chip)
        }
        wrap.appendChild(list)
        return wrap
      }

      // Resting state: a random subset of the FAQ questions. Skipped once a
      // conversation has started so a variant re-resolve can't wipe it.
      _renderStarterChips() {
        const ask = this._ask
        if (!ask || this._conversing) return
        const label = this.dataset.chipsLabel || 'Popular questions'
        const block = this._buildChips(label, shuffle(this._faqPool || []), this._maxChips())
        ask.body.replaceChildren(...(block ? [block] : []))
      }

      _ask_buildTargetingContext() {
        const spectrumAi = window.__spectrumAi || null
        const builder =
          spectrumAi?.targeting && typeof spectrumAi.targeting.buildContext === 'function'
            ? spectrumAi.targeting.buildContext
            : null
        if (builder) {
          try {
            return builder()
          } catch (_) {
            /* fall through */
          }
        }
        let device = 'desktop'
        const w = window.innerWidth || 0
        if (w > 0 && w < 768) device = 'mobile'
        else if (w >= 768 && w < 1024) device = 'tablet'
        const customer = spectrumAi?.customer || null
        return {
          page: { type: 'product', url: window.location.href, path: window.location.pathname },
          device: { type: device },
          currentUser: customer
            ? { isAnonymous: false, id: String(customer.id), email: customer.email }
            : { isAnonymous: true },
        }
      }

      _ask_setBusy(busy) {
        const ask = this._ask
        if (!ask) return
        ask.inflight = busy
        if (ask.sendBtn) ask.sendBtn.disabled = busy
        ask.input.disabled = busy
      }

      _scrollToLatest() {
        const ask = this._ask
        if (ask) ask.body.scrollTop = ask.body.scrollHeight
      }

      _renderUser(question) {
        const row = el('div', 'sai-outywjjt__user')
        const bubble = el('div', 'sai-outywjjt__user-bubble')
        bubble.textContent = question
        row.appendChild(bubble)
        return row
      }

      _renderTyping() {
        const row = el('div', 'sai-outywjjt__row sai-outywjjt__row--typing')
        row.appendChild(this._botAvatar())
        const typing = el('div', 'sai-outywjjt__typing')
        typing.innerHTML = '<span></span><span></span><span></span>'
        row.appendChild(typing)
        return row
      }

      _ask_submit(question) {
        const ask = this._ask
        if (!ask || ask.inflight) return
        const trimmed = (question || '').trim()
        if (!trimmed) return

        const now = Date.now()
        if (now < ask.rateLimitedUntil) {
          const waitS = Math.ceil((ask.rateLimitedUntil - now) / 1000)
          this._conversing = true
          ask.body.replaceChildren(
            this._renderUser(trimmed),
            this._errorRow(`Please wait ${waitS}s before asking another question.`, trimmed, false),
          )
          this._scrollToLatest()
          return
        }

        // Intent signal — fires BEFORE the fetch. Shares the `faqs` feature
        // slug with f4qpdp01; snippet_id in the envelope distinguishes them.
        // product_id is the PDP product SSR'd onto data-product-id.
        this._track(`${FEATURE_SLUG}:ask_elem_clicked`, {
          question_text: trimmed,
          product_id: this.getAttribute('data-product-id') || '',
        })

        this._conversing = true
        ask.input.value = ''
        ask.body.replaceChildren(this._renderUser(trimmed), this._renderTyping())
        this._scrollToLatest()
        this._ask_setBusy(true)

        const body = {
          question: trimmed,
          product: { id: ask.productId, handle: ask.productHandle },
          targetingContext: this._ask_buildTargetingContext(),
        }

        fetch(`${ask.proxyBase}/faq/ask`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
          .then((res) => {
            if (res.status === 429) {
              const retryAfter = Number.parseInt(res.headers.get('Retry-After') || '0', 10)
              if (retryAfter > 0) ask.rateLimitedUntil = Date.now() + retryAfter * 1000
            }
            return res.json().then((json) => ({ res, json }))
          })
          .then((out) => {
            const json = out.json || {}
            if (out.res.ok && json.success === true && json.data) {
              this._renderSuccess(trimmed, json.data.answer?.html, json.data.questionId)
            } else {
              const status = out.res.status
              this._renderError(trimmed, status === 429 || status >= 500)
            }
          })
          .catch(() => {
            this._renderError(trimmed, true)
          })
          .then(() => {
            this._ask_setBusy(false)
          })
      }

      _renderSuccess(question, answerHtml, questionId) {
        const ask = this._ask
        if (!ask) return
        const row = el('div', 'sai-outywjjt__row sai-outywjjt__row--answer')
        row.appendChild(this._botAvatar())
        const answer = el('div', 'sai-outywjjt__answer')
        // Trusted server-rendered HTML — see trust boundary note above.
        answer.innerHTML = answerHtml || ''
        // Feedback control inside the answer bubble. Only when the server
        // returned a questionId (== the faq_question_log row id) to rate against.
        if (questionId) answer.appendChild(this._ask_feedback(questionId))
        row.appendChild(answer)

        // The answer stands alone — no post-answer follow-up chips.
        ask.body.replaceChildren(this._renderUser(question), row)
        this._scrollToLatest()
      }

      // "Was this helpful?" thumbs on an answer. One vote per answer (the whole
      // exchange is replaced on the next question anyway); a failed POST is
      // swallowed — feedback is best-effort and must never disrupt the shopper.
      _ask_feedback(questionId) {
        const ask = this._ask
        const wrap = el('div', 'sai-outywjjt__feedback')
        const label = el('span', 'sai-outywjjt__feedback-label')
        label.textContent = 'Was this helpful?'
        wrap.appendChild(label)

        const vote = (rating, btn) => {
          if (wrap.getAttribute('data-voted')) return
          wrap.setAttribute('data-voted', rating)
          btn.classList.add('sai-outywjjt__feedback-btn--active')
          label.textContent = 'Thanks for the feedback'
          this._track(`${FEATURE_SLUG}:feedback_elem_clicked`, {
            question_id: questionId,
            rating,
            product_id: this.getAttribute('data-product-id') || '',
          })
          fetch(`${ask.proxyBase}/faq/feedback`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ questionId, rating }),
          }).catch(() => {})
        }

        const mk = (rating, text, aria) => {
          const b = el('button', 'sai-outywjjt__feedback-btn', { type: 'button', 'aria-label': aria })
          b.textContent = text
          b.addEventListener('click', () => vote(rating, b))
          return b
        }
        wrap.appendChild(mk('helpful', 'Yes', 'Yes, this was helpful'))
        wrap.appendChild(mk('unhelpful', 'No', 'No, this was not helpful'))
        return wrap
      }

      _errorRow(message, question, retryable) {
        const row = el('div', 'sai-outywjjt__row sai-outywjjt__row--error')
        const avatar = el('div', 'sai-outywjjt__err-avatar', { 'aria-hidden': 'true' })
        avatar.textContent = '!'
        row.appendChild(avatar)

        const box = el('div', 'sai-outywjjt__err')
        const msg = el('div', 'sai-outywjjt__err-msg')
        msg.textContent = message
        box.appendChild(msg)

        if (retryable) {
          const retry = el('button', 'sai-outywjjt__retry', {
            type: 'button',
            'data-question': question,
          })
          retry.innerHTML = `${RETRY_SVG}Retry`
          box.appendChild(retry)
        }
        row.appendChild(box)
        return row
      }

      _renderError(question, retryable) {
        const ask = this._ask
        if (!ask) return
        ask.body.replaceChildren(
          this._renderUser(question),
          this._errorRow("Sorry — I couldn't load that answer. Please try again.", question, retryable),
        )
        this._scrollToLatest()
      }

      // Variant resolution entry point. Updates presentation + the resting
      // chips (from the FAQ items in `content`). Called by the bind callback on
      // every $spectrum:variant_resolved event.
      applyVariant(presentation, content) {
        if (presentation && typeof presentation === 'object') {
          if (typeof presentation.header === 'string') {
            const h = this.querySelector('.sai-outywjjt__title')
            if (h) h.textContent = presentation.header
            const input = this.querySelector('.sai-outywjjt__input')
            if (input) input.setAttribute('aria-label', presentation.header)
          }
          if (typeof presentation.subheading === 'string') {
            const text = this.querySelector('.sai-outywjjt__status-text')
            if (text) text.textContent = presentation.subheading
          }
          if (typeof presentation.input_placeholder === 'string') {
            const input = this.querySelector('.sai-outywjjt__input')
            if (input) input.placeholder = presentation.input_placeholder
          }
          if (typeof presentation.chips_label === 'string') {
            this.dataset.chipsLabel = presentation.chips_label
          }
          if (presentation.max_chips != null) {
            const n = Number.parseInt(presentation.max_chips, 10)
            if (n > 0) this.dataset.maxChips = String(n)
          }
          if (
            typeof presentation.mark_gradient_start === 'string' &&
            typeof presentation.mark_gradient_end === 'string'
          ) {
            this.style.setProperty(
              '--sai-outywjjt-mark',
              `linear-gradient(135deg, ${presentation.mark_gradient_start}, ${presentation.mark_gradient_end})`,
            )
          }
          if (typeof presentation.show_branding === 'boolean') {
            this._localShowBranding = presentation.show_branding
            this._syncCapabilityVisibility()
          }
        }

        if (content && Array.isArray(content.items)) {
          this._faqPool = content.items
            .map((it) => (it && typeof it.question === 'string' ? it.question : ''))
            .filter((q) => q.trim())
          this._renderStarterChips()
        }
      }
    }
    customElements.define(TAG, SaiAskAi)
  }

  // ── Test surface — pure helpers exposed for unit tests when the harness
  // flag is set. Production never sets the flag.
  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiOutywjjt = {
      safeTrack,
      shuffle,
    }
  }

  function bindAllContainers() {
    const api = window.__spectrumAi?.snippet
    const containers = document.querySelectorAll(
      `[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`,
    )

    if (!api?.bind) {
      // No-bind fallback: the custom element's _initAsk() already hid the
      // widget when window.__spectrumAi was absent. Nothing more to do here.
      return
    }

    for (const node of containers) {
      const handles = api.bind(node, ({ variant, entry }) => {
        if (!variant?.content) return
        const root = node.querySelector(TAG)
        if (root && typeof root.applyVariant === 'function') {
          root.applyVariant(variant.content, entry?.content)
        }
      })
      const root = node.querySelector(TAG)
      if (root && handles?.track && typeof root.setAnalytics === 'function') {
        root.setAnalytics(handles.track)
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAllContainers, { once: true })
  } else {
    bindAllContainers()
  }
})()
